// supervisor-transport-tick.ts
//
// P7 §7 wiring: runs outbox-delivery-worker.ts + directive-inbox-mattermost-
// sync.ts as ONE non-blocking step per control-loop tick. When no Mattermost
// transport is configured (`deps` undefined — no bot token/credentials
// resolved for this mission), this is a true no-op: the mission still works
// end-to-end via CLI-local directives only (directive-inbox.ts's
// `writeDirective` never required a Mattermost session), which is what
// "local-only mode" means here — there is no separate code path to invoke,
// simply the ABSENCE of these two calls.
//
// "Tick không block": both calls below are already single-pass drains (no
// internal sleep/poll loop of their own) — `runOutboxDeliveryWorkerOnce`
// backs off WITHIN its own bounded attempt loop only, and
// `syncMattermostDirectivesIntoInbox` reads pages back to the cursor (CXR-07):
// usually one page, more only after a backlog — bounded by MAX_POLL_PAGES,
// past which it throws rather than skip posts. No sleep/poll loop of their
// own, so calling both once per tick keeps the control loop responsive.

import {
  type SyncMattermostDirectivesInput,
  syncMattermostDirectivesIntoInbox,
} from "./directive-inbox-mattermost-sync.js";
import {
  type OutboxDeliveryWorkerRunResult,
  type OutboxSendFn,
  runOutboxDeliveryWorkerOnce,
} from "./outbox-delivery-worker.js";

export interface MattermostTransportDeps {
  /** MUST be reconciliation-aware for `mattermost_post` kind ops — see
   * outbox-delivery-worker.ts's H1 fix note (never a raw `createPost`). */
  outboxSend: OutboxSendFn;
  outboxMaxAttemptsPerTick: number;
  /** In-tick retry backoff base (ms), from effective-config's
   * `outbox_retry_backoff_ms` — never a literal here (M1 fix). */
  outboxRetryBackoffMs: number;
  directiveSync: Omit<
    SyncMattermostDirectivesInput,
    "controlRoot" | "machineLockPath" | "missionId"
  >;
}

export interface RunTransportTickInput {
  controlRoot: string;
  machineLockPath: string;
  missionId: string;
  /** undefined => local-only mode (no Mattermost credentials for this mission). */
  mattermost: MattermostTransportDeps | undefined;
}

export type TransportTickResult =
  | { mode: "local_only" }
  | { mode: "mattermost"; outbox: OutboxDeliveryWorkerRunResult };

/**
 * One tick's worth of transport work. Safe to call every control-loop
 * iteration — both sub-steps are idempotent/backlog-preserving on their own
 * (outbox worker re-lists pending ops each call; directive sync's cursor
 * persists past only what it successfully wrote to the inbox).
 */
export async function runTransportTick(input: RunTransportTickInput): Promise<TransportTickResult> {
  if (input.mattermost === undefined) {
    return { mode: "local_only" };
  }

  const mattermost = input.mattermost;
  const drainOutbox = () =>
    runOutboxDeliveryWorkerOnce({
      controlRoot: input.controlRoot,
      send: mattermost.outboxSend,
      maxAttemptsPerTick: mattermost.outboxMaxAttemptsPerTick,
      backoffBaseMs: mattermost.outboxRetryBackoffMs,
    });
  const outbox = await drainOutbox();

  const { repliesRecorded } = await syncMattermostDirectivesIntoInbox({
    ...mattermost.directiveSync,
    controlRoot: input.controlRoot,
    machineLockPath: input.machineLockPath,
    missionId: input.missionId,
  });

  // Review 261006 H1: a chat reply (`@avatar status`, legacy `answer`) is
  // recorded by the sync above. `run` may return right after this tick
  // (blocked / waiting / quota-sleep — exactly when someone asks for status),
  // so deliver it now instead of at the start of the NEXT `run`.
  if (repliesRecorded === 0) return { mode: "mattermost", outbox };
  const again = await drainOutbox();
  return {
    mode: "mattermost",
    outbox: {
      sent: [...outbox.sent, ...again.sent],
      retried: [...outbox.retried, ...again.retried],
      connectionFixNeeded: [...outbox.connectionFixNeeded, ...again.connectionFixNeeded],
      uncertain: again.uncertain,
    },
  };
}
